import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, CreditCard, LockKeyhole, MapPin, Package, RefreshCw, ShoppingBag, Truck } from "lucide-react";

import PublicNavbar from "../../components/PublicNavbar";
import { useCart } from "../../../cart/useCart";
import { cartItemsForCheckout, readCheckoutContext } from "../../../checkout/checkoutContext";
import { createCheckoutPaymentIntent, saveCheckoutContact } from "../../../checkout/checkoutPublicApi";
import { fetchPublicSettings } from "../../../settings/settingsPublicApi";
import "./checkout-page.css";

const stripeScriptUrl = "https://js.stripe.com/v3/";

function money(value) {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
}

function loadStripeJs() {
  if (window.Stripe) return Promise.resolve(window.Stripe);
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${stripeScriptUrl}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.Stripe));
      existing.addEventListener("error", reject);
      return;
    }
    const script = document.createElement("script");
    script.src = stripeScriptUrl;
    script.async = true;
    script.onload = () => resolve(window.Stripe);
    script.onerror = reject;
    document.body.appendChild(script);
  });
}


function formatRequestedDelivery(schedule) {
  if (!schedule?.date || !schedule?.time) return "Not selected";
  const date = new Date(`${schedule.date}T${schedule.time}:00`);
  if (Number.isNaN(date.getTime())) return `${schedule.date} at ${schedule.time}`;
  return `${date.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })} at ${date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
}

function formatAddress(address, result) {
  if (result?.formatted_address) return result.formatted_address;
  if (!address) return "Delivery address";
  return [address.street, address.apartment, address.city, address.state, address.zip].filter(Boolean).join(", ");
}

export default function CheckoutPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const context = location.state || readCheckoutContext();
  const { items, itemCount, subtotal, reconcileInvalidItems } = useCart();
  const paymentRef = useRef(null);
  const mountedPaymentRef = useRef(null);
  const preparedSignatureRef = useRef("");
  const [contact, setContact] = useState({ firstName: "", lastName: "", email: "", phone: "" });
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [checkoutData, setCheckoutData] = useState(null);
  const [stripeClient, setStripeClient] = useState(null);
  const [stripeElements, setStripeElements] = useState(null);
  const [paymentElementReady, setPaymentElementReady] = useState(false);
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [prepareNonce, setPrepareNonce] = useState(0);
  const [feeSettings, setFeeSettings] = useState({ serviceFee: 0, taxRate: 0 });

  const checkoutItems = useMemo(() => cartItemsForCheckout(items), [items]);
  const preparationSignature = useMemo(() => JSON.stringify({
    items: checkoutItems,
    deliveryStatus: context?.deliveryResult?.status || "",
    deliveryFee: context?.deliveryResult?.delivery_fee || "0.00",
    promoCode: context?.promoCode || "",
    nonce: prepareNonce,
  }), [checkoutItems, context, prepareNonce]);

  useEffect(() => {
    if (!context || !items.length || !["free", "paid"].includes(context.deliveryResult?.status) || !context.deliverySchedule?.date || !context.deliverySchedule?.time) {
      navigate("/cart", { replace: true });
    }
  }, [context, items.length, navigate]);

  useEffect(() => () => {
    if (mountedPaymentRef.current) mountedPaymentRef.current.unmount();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchPublicSettings({ signal: controller.signal })
      .then((settings) => {
        setFeeSettings({
          serviceFee: money(settings?.payments?.service_fee),
          taxRate: money(settings?.payments?.sales_tax_percentage ?? settings?.payments?.default_tax_rate),
        });
      })
      .catch(() => {});
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!context || !items.length || !["free", "paid"].includes(context.deliveryResult?.status) || !context.deliverySchedule?.date || !context.deliverySchedule?.time) return;
    if (preparedSignatureRef.current === preparationSignature) return;
    preparedSignatureRef.current = preparationSignature;
    preparePayment();
    // preparePayment is intentionally keyed by preparationSignature so contact edits do not create duplicate pending Orders/PaymentIntents.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, items.length, preparationSignature]);

  if (!context) return null;

  const serverOrder = checkoutData?.order;
  const deliveryFee = serverOrder ? money(serverOrder.delivery_fee) : (context.deliveryResult?.status === "paid" ? money(context.deliveryResult?.delivery_fee) : 0);
  const promoDiscount = serverOrder ? money(serverOrder.discount_amount) : money(context.appliedPromo?.merchandise_discount || context.appliedPromo?.total_discount);
  const serviceFee = serverOrder ? money(serverOrder.service_fee) : (items.length ? feeSettings.serviceFee : 0);
  const taxRate = serverOrder ? money(serverOrder.tax_rate_snapshot) : feeSettings.taxRate;
  const discountedSubtotal = Math.max(subtotal - promoDiscount, 0);
  const tax = serverOrder ? money(serverOrder.tax_amount) : discountedSubtotal * (taxRate / 100);
  const total = serverOrder ? money(serverOrder.total_amount) : discountedSubtotal + deliveryFee + serviceFee + tax;
  const contactComplete = contact.firstName.trim() && contact.lastName.trim() && contact.email.trim() && contact.phone.trim();
  const canPlaceOrder = Boolean(contactComplete && items.length && context.deliverySchedule?.date && context.deliverySchedule?.time && ["free", "paid"].includes(context.deliveryResult?.status) && stripeClient && stripeElements && paymentElementReady && status !== "submitting");


  function cartChangedMessage(data) {
    const labels = (data?.invalid_items || [])
      .map((entry) => entry.label)
      .filter(Boolean)
      .slice(0, 2);
    if (labels.length === 1) return `${labels[0]} is no longer available and was removed from your cart.`;
    return data?.message || "One or more items in your cart are no longer available and were removed.";
  }

  function handleCartChanged(data) {
    reconcileInvalidItems(data?.invalid_items || []);
    setStripeClient(null);
    setStripeElements(null);
    setCheckoutData(null);
    setStatus("error");
    const cartNotice = cartChangedMessage(data);
    setMessage(cartNotice);
    navigate("/cart", { replace: true, state: { cartNotice } });
  }

  function updateContact(field, value) {
    setContact((current) => ({ ...current, [field]: value }));
  }

  function buildPreparationPayload() {
    return {
      delivery_address: context.deliveryAddress,
      order_notes: context.orderNote || "",
      promo_code: context.promoCode || "",
      requested_delivery_date: context.deliverySchedule?.date || "",
      requested_delivery_time: context.deliverySchedule?.time || "",
      items: checkoutItems,
    };
  }

  async function preparePayment() {
    setStatus("preparing");
    setMessage("");
    setPaymentElementReady(false);
    try {
      const data = await createCheckoutPaymentIntent(buildPreparationPayload());
      setCheckoutData(data);
      const Stripe = await loadStripeJs();
      const stripe = Stripe(data.publishable_key);
      const elements = stripe.elements({
        clientSecret: data.client_secret,
        appearance: {
          theme: "stripe",
          variables: {
            colorPrimary: "#db2f7d",
            colorText: "#341623",
            colorDanger: "#b4234b",
            borderRadius: "8px",
            fontFamily: "Inter, system-ui, sans-serif",
          },
        },
      });
      if (mountedPaymentRef.current) mountedPaymentRef.current.unmount();
      const paymentElement = elements.create("payment", {
        layout: { type: "tabs" },
        paymentMethodOrder: ["card", "us_bank_account"],
        wallets: {
          applePay: "never",
          googlePay: "never",
        },
      });
      paymentElement.on("ready", () => setPaymentElementReady(true));
      paymentElement.mount(paymentRef.current);
      mountedPaymentRef.current = paymentElement;
      setStripeClient(stripe);
      setStripeElements(elements);
      setStatus("ready");
    } catch (error) {
      console.error("SugaLumps checkout preparation failed", error);
      if (error.data?.code === "cart_changed") {
        handleCartChanged(error.data);
        return;
      }
      setStripeClient(null);
      setStripeElements(null);
      setCheckoutData(null);
      setStatus("error");
      setMessage(error.message || "We couldn't prepare secure payment. Please try again.");
    }
  }

  async function confirmPayment(event) {
    event.preventDefault();
    if (!canPlaceOrder || !checkoutData?.order?.id) return;
    setStatus("submitting");
    setMessage("");
    try {
      await saveCheckoutContact(checkoutData.order.id, {
        checkout_token: checkoutData.checkout_token,
        first_name: contact.firstName,
        last_name: contact.lastName,
        email: contact.email,
        phone: contact.phone,
        marketing_opt_in: marketingOptIn,
      });
    } catch (error) {
      console.error("SugaLumps checkout contact save failed", error);
      setStatus("ready");
      setMessage(error.message || "We couldn't save your contact information. Please try again.");
      return;
    }
    const successUrl = `${window.location.origin}/payment/success?order=${checkoutData.order.id}&token=${checkoutData.checkout_token}`;
    const result = await stripeClient.confirmPayment({
      elements: stripeElements,
      confirmParams: {
        return_url: successUrl,
        payment_method_data: {
          billing_details: {
            name: `${contact.firstName} ${contact.lastName}`.trim(),
            email: contact.email,
            phone: contact.phone,
          },
        },
      },
      redirect: "if_required",
    });
    if (result.error) {
      setStatus("ready");
      setMessage(result.error.message || "Payment could not be completed.");
      return;
    }
    if (result.paymentIntent?.status === "succeeded") {
      navigate(`/payment/success?order=${checkoutData.order.id}&token=${checkoutData.checkout_token}`);
    } else if (result.paymentIntent?.status === "canceled") {
      navigate("/payment/failed");
    } else {
      setStatus("ready");
      setMessage("Payment is processing. Your order will update when Stripe confirms it.");
    }
  }

  return (
    <div className="sl-checkout-page">
      <PublicNavbar />
      <main className="sl-checkout-main">
        <section className="sl-checkout-heading">
          <div>
            <span className="sl-checkout-eyebrow">Checkout</span>
            <h1>Complete your order</h1>
            <p>Secure payment and delivery confirmation.</p>
          </div>
          <Link to="/cart" className="sl-checkout-back-link"><ArrowLeft size={15} strokeWidth={1.8} /> Back to Cart</Link>
        </section>

        {status === "complete" ? (
          <section className="sl-checkout-card sl-checkout-complete">
            <Check size={38} strokeWidth={1.7} />
            <h2>Order confirmed</h2>
            <p>{message}</p>
            <Link to="/menu" className="sl-checkout-primary">Back to Menu</Link>
          </section>
        ) : (
          <form className="sl-checkout-layout" onSubmit={confirmPayment}>
            <div className="sl-checkout-content">
              <section className="sl-checkout-card">
                <div className="sl-checkout-card-title"><ShoppingBag size={18} /><h2>Contact Information</h2></div>
                <div className="sl-checkout-field-grid">
                  <label><span>First Name</span><input value={contact.firstName} onChange={(event) => updateContact("firstName", event.target.value)} required /></label>
                  <label><span>Last Name</span><input value={contact.lastName} onChange={(event) => updateContact("lastName", event.target.value)} required /></label>
                  <label><span>Email</span><input type="email" value={contact.email} onChange={(event) => updateContact("email", event.target.value)} required /></label>
                  <label><span>Phone</span><input value={contact.phone} onChange={(event) => updateContact("phone", event.target.value)} required /></label>
                </div>
                <label className="sl-checkout-opt-in"><input type="checkbox" checked={marketingOptIn} onChange={(event) => setMarketingOptIn(event.target.checked)} /><span>Email me SugaLumps updates and offers.</span></label>
              </section>

              <section className="sl-checkout-card">
                <div className="sl-checkout-card-title"><MapPin size={18} /><h2>Delivery Address</h2></div>
                <div className="sl-checkout-address-box">
                  <Truck size={18} strokeWidth={1.7} />
                  <div>
                    <strong>{context.deliveryResult?.status === "free" ? "Free delivery" : "Delivery fee applies"}</strong>
                    <p>{formatAddress(context.deliveryAddress, context.deliveryResult)}</p>
                    <small className="sl-checkout-requested-delivery">Requested delivery: {formatRequestedDelivery(context.deliverySchedule)}</small>
                    {context.deliveryResult?.duration_minutes && <small>Approximately {context.deliveryResult.duration_minutes} minutes away</small>}
                  </div>
                  <Link to="/cart" className="sl-checkout-change-link">Change</Link>
                </div>
              </section>

              <section className="sl-checkout-card">
                <div className="sl-checkout-card-title"><CreditCard size={18} /><h2>Payment</h2></div>
                <div className={`sl-payment-element ${status === "error" ? "is-error" : ""}`}>
                  {status === "preparing" && <p className="sl-payment-state">Preparing secure payment...</p>}
                  {status === "error" ? (
                    <div className="sl-payment-error">
                      <p>{message}</p>
                      <button type="button" onClick={() => setPrepareNonce((value) => value + 1)}><RefreshCw size={14} /> Try Again</button>
                    </div>
                  ) : (
                    <div ref={paymentRef} />
                  )}
                </div>
              </section>
            </div>

            <aside className="sl-checkout-summary sl-summary-card">
              <div className="sl-summary-title"><Package size={19} strokeWidth={1.7} /><h2>Order Summary</h2></div>
              <div className="sl-summary-lines">
                <div className="sl-summary-line"><span>Subtotal ({itemCount} items)</span><strong>${subtotal.toFixed(2)}</strong></div>
                {promoDiscount > 0 && <div className="sl-summary-line sl-summary-promo-line"><span>Promo ({serverOrder?.coupon_code_snapshot || context.promoCode})</span><strong>-${promoDiscount.toFixed(2)}</strong></div>}
                <div className="sl-summary-line"><span>Delivery</span><strong>{deliveryFee > 0 ? `$${deliveryFee.toFixed(2)}` : "Free"}</strong></div>
                <div className="sl-summary-line"><span>Service Fee</span><strong>${serviceFee.toFixed(2)}</strong></div>
                <div className="sl-summary-line"><span>Sales Tax ({taxRate.toFixed(1)}%)</span><strong>${tax.toFixed(2)}</strong></div>
              </div>
              <div className="sl-summary-total"><span>Total</span><strong>${total.toFixed(2)}</strong></div>
              <div className="sl-secure-note"><LockKeyhole size={13} strokeWidth={1.7} /> Card details are handled securely by Stripe.</div>
              {message && status !== "error" && <p className={`sl-checkout-message sl-checkout-message--${status}`}>{message}</p>}
              <button type="submit" className="sl-checkout-button" disabled={!canPlaceOrder}>
                <LockKeyhole size={15} strokeWidth={1.8} />
                {status === "submitting" ? "Placing Order..." : `Place Order • $${total.toFixed(2)}`}
              </button>
            </aside>
          </form>
        )}
      </main>
    </div>
  );
}
